import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { deleteDoc, deleteField, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db, app, API_BASE_URL } from '../../firebase/firebaseConfig';
import {
  DetailShell,
  HCard,
  SerifHeading,
  SectionLabel,
  HPrimaryButton,
  HSecondaryButton,
  HGhostButton,
  HChip,
} from '../h';
import { useHiglos } from './useHiglos';
import { cn } from '../../lib/utils';

/* v9 5.7 — Position picker.

   Behaviour:
     - Tap a position row → sets position on that row (writes
       familyInfo/{familyID}/igloos/{iglooID}.position = 'Left'|'Centre'|'Right').
     - Tap the row that's already ticked → CLEARS the position
       (deleteField) — same effect as the old bottom "Remove" button. The
       Hearth stays a member of the Higlo, it just doesn't show on the TV.
     - Bottom button → fully removes this Hearth from the Higlo
       (calls /user/removeMemberFromIgloo, nav back to HigloDetail).

   Disabled-state rule for the bottom button: if this is the only
   controller of the Higlo, we refuse client-side — removing the last
   controller would leave the Higlo with no one who can manage it.

   Route: /app/higlos/:iglooID/position/:familyID
*/
const POSITIONS = [
  { id: 'Left',   subtitle: 'Appears on the left of the TV' },
  { id: 'Centre', subtitle: 'Appears in the middle of the TV' },
  { id: 'Right',  subtitle: 'Appears on the right of the TV' },
];

export function PositionPicker({
  accountUUID,
  owningFamilies,
  primaryFamilyID,
  iglooID: iglooIDProp,
  familyID: familyIDProp,
}) {
  const params = useParams();
  const navigate = useNavigate();
  const iglooID = iglooIDProp || params.iglooID;
  const familyID = familyIDProp || params.familyID;

  const higlos = useHiglos({ owningFamilies, accountUUID, primaryFamilyID });
  const higlo = higlos[iglooID];
  const membership = (higlo?.ownedMemberships || []).find(
    (m) => m.familyID === familyID,
  );
  const currentPosition = membership?.position || null;
  const hearthName = membership?.familyName || 'this Hearth';
  const thisHearthHigloLabel = membership?.iglooLabel || higlo?.name || '';

  const [renamingHigloOnHearth, setRenamingHigloOnHearth] = useState(false);
  const [pendingHigloLabel, setPendingHigloLabel] = useState('');
  const [savingHigloLabel, setSavingHigloLabel] = useState(false);

  const beginRenameHigloOnHearth = () => {
    setPendingHigloLabel(thisHearthHigloLabel || '');
    setRenamingHigloOnHearth(true);
  };
  const cancelRenameHigloOnHearth = () => {
    setRenamingHigloOnHearth(false);
    setPendingHigloLabel('');
  };
  const saveRenameHigloOnHearth = async () => {
    const trimmed = (pendingHigloLabel || '').trim();
    if (!trimmed || trimmed === thisHearthHigloLabel) {
      cancelRenameHigloOnHearth();
      return;
    }
    setSavingHigloLabel(true);
    try {
      await updateDoc(doc(db, 'familyInfo', familyID, 'igloos', iglooID), {
        name: trimmed,
      });
      cancelRenameHigloOnHearth();
    } catch (err) {
      console.error('per-Hearth Higlo rename error', err);
    } finally {
      setSavingHigloLabel(false);
    }
  };

  const controllers = higlo?.controllers || [];
  const members = higlo?.members || [];
  const allFamiliesInHiglo = [...new Set([...controllers, ...members])];
  const isOnlyController =
    controllers.length === 1 && controllers[0] === familyID;
  // Mirrors OLD ConfigureHiglos.canLeaveIgloo — can't leave if you're the
  // last family in the Higlo. The Higlo would be orphaned with no members
  // and nobody could re-add anyone, so refuse and steer them to Delete.
  const isLastFamily =
    allFamiliesInHiglo.length <= 1 && allFamiliesInHiglo[0] === familyID;
  const leaveBlockedReason = isOnlyController
    ? 'This Hearth is the only Controller. Make another family a Controller before removing this Hearth.'
    : isLastFamily
      ? 'This Hearth is the only one in the Higlo. Delete the Higlo instead.'
      : null;

  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null); // 'Left'|'Centre'|'Right'|'clear'|'remove'
  const [removeError, setRemoveError] = useState('');
  const [positionError, setPositionError] = useState('');

  // Return to wherever the user came from. HearthDetail and HigloDetail
  // both link into PositionPicker, so a hard-coded /app/higlos/{iglooID}
  // sent the user to the wrong screen when they entered from
  // HearthDetail. navigate(-1) walks the browser history; for a deep
  // link with no history we fall back to the Higlo detail.
  const back = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(`/app/higlos/${iglooID}`);
  };

  // Clear this Hearth's position on this Higlo. Matches the OLD webapp
  // ConfigureHiglos.handlePositionChange(null): position field deleted from
  // the igloos doc, and the activeIgloos subcollection entry deleted.
  const clearPosition = async () => {
    await updateDoc(doc(db, 'familyInfo', familyID, 'igloos', iglooID), {
      position: deleteField(),
    });
    await deleteDoc(
      doc(db, 'familyInfo', familyID, 'activeIgloos', iglooID),
    ).catch(() => {});
  };

  // Set the position on this Higlo, matching the OLD webapp's semantics:
  //   1. If this family is currently at the same position in any OTHER
  //      Higlo, deactivate that one first (clear its position + activeIgloos).
  //      A physical position (Left/Centre/Right) can only belong to one Higlo
  //      at a time for a given Hearth.
  //   2. Enforce max 3 active Higlos per family (there are only 3 positions).
  //   3. Write the new position + create the activeIgloos entry.
  const setPosition = async (pos) => {
    // 1. Find and deactivate any conflict at the same position on OTHER higlos.
    const conflicts = Object.values(higlos || {}).filter((h) => {
      if (!h || h.iglooID === iglooID) return false;
      const m = (h.ownedMemberships || []).find((x) => x.familyID === familyID);
      return m && m.position === pos;
    });
    for (const h of conflicts) {
      await updateDoc(
        doc(db, 'familyInfo', familyID, 'igloos', h.iglooID),
        { position: deleteField() },
      );
      await deleteDoc(
        doc(db, 'familyInfo', familyID, 'activeIgloos', h.iglooID),
      ).catch(() => {});
    }

    // 2. Max-3 active-igloos guard (after deactivating conflicts, before
    //    activating the new one). If the current Higlo is already active for
    //    this family we're just moving the marker, so skip the check.
    const activeCount = Object.values(higlos || {}).filter((h) => {
      if (!h || h.iglooID === iglooID) return false;
      if (conflicts.some((c) => c.iglooID === h.iglooID)) return false;
      const m = (h.ownedMemberships || []).find((x) => x.familyID === familyID);
      return m && m.position;
    }).length;
    if (activeCount >= 3 && !currentPosition) {
      setPositionError('Maximum 3 active Higlos per Hearth.');
      return false;
    }

    // 3. Write the new position + activate.
    await updateDoc(doc(db, 'familyInfo', familyID, 'igloos', iglooID), {
      position: pos,
    });
    await setDoc(
      doc(db, 'familyInfo', familyID, 'activeIgloos', iglooID),
      {},
    );
    return true;
  };

  const onTapPosition = async (pos) => {
    if (saving) return;
    if (!familyID || !iglooID) return;
    setPositionError('');
    setSaving(true);
    try {
      if (pos === currentPosition) {
        // Toggle off — same effect as the OLD bottom "Remove" button.
        // Position field cleared + activeIgloos deleted. Keeps membership.
        setPending('clear');
        await clearPosition();
      } else {
        setPending(pos);
        await setPosition(pos);
      }
    } catch (err) {
      console.error('PositionPicker onTapPosition error', err);
      setPositionError('Could not save. Try again?');
    } finally {
      setSaving(false);
      setPending(null);
    }
  };

  const removeHearthFromHiglo = async () => {
    if (!familyID || !iglooID) return;
    if (leaveBlockedReason) {
      setRemoveError(leaveBlockedReason);
      return;
    }
    setSaving(true);
    setPending('remove');
    setRemoveError('');
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${API_BASE_URL}/user/removeMemberFromIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          iglooID,
          memberFamilyID: familyID,    // target — this Hearth, removing itself
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        setRemoveError(data?.error || 'Could not remove. Try again?');
        setSaving(false);
        setPending(null);
        return;
      }
      back();
    } catch (err) {
      console.error('PositionPicker remove error', err);
      setRemoveError('Could not remove. Try again?');
      setSaving(false);
      setPending(null);
    }
  };

  return (
    <DetailShell
      onBack={back}
      breadcrumb={higlo?.name || 'Back to Higlo'}
    >
      <div className="px-4 pb-6">
        <SerifHeading size="lg" className="mb-1 mt-2">
          {higlo?.name || 'this Higlo'} on {hearthName}
        </SerifHeading>
        <p className="text-[13px] text-slate-500 leading-relaxed mb-5">
          Position, name, and membership for this Higlo on this Hearth.
        </p>

        <SectionLabel className="mb-2">Where this Higlo appears</SectionLabel>
        <p className="text-[12px] text-slate-500 leading-relaxed mb-2">
          Pick where this Higlo appears on the TV for{' '}
          <strong>{hearthName}</strong>. Tap the ticked row again to stop
          showing it on the TV without leaving the Higlo.
        </p>

        <HCard className="mb-2 overflow-hidden">
          {POSITIONS.map((p, idx) => {
            const isCurrent = currentPosition === p.id;
            const isLast = idx === POSITIONS.length - 1;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onTapPosition(p.id)}
                disabled={saving && pending === p.id}
                className={cn(
                  'w-full px-4 py-3.5 flex items-center gap-4 text-left transition-colors',
                  !isLast && 'border-b border-slate-100',
                  isCurrent ? 'bg-slate-50' : 'hover:bg-slate-50',
                  'focus-visible:outline-none focus-visible:bg-slate-50',
                )}
              >
                <BigPositionMini
                  position={p.id}
                  inverted={isCurrent}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[14px] font-semibold text-slate-900">{p.id}</div>
                    {isCurrent && (
                      <HChip className="bg-indigo-50 text-indigo-700 border-indigo-200">
                        Current
                      </HChip>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500">{p.subtitle}</div>
                </div>
                {isCurrent && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#0f172a"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="flex-shrink-0"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </HCard>

        <p className="text-[11px] text-slate-400 leading-relaxed mb-5">
          Other families in this Higlo see this Hearth in this position.
        </p>
        {positionError && (
          <p className="text-[12px] text-red-600 leading-relaxed text-center mb-3">
            {positionError}
          </p>
        )}

        {/* Per-Hearth Higlo rename — affects only this Hearth's label for
            this Higlo. Other Hearths in the account keep their own labels. */}
        <SectionLabel className="mb-2">
          What does {hearthName} call this Higlo?
        </SectionLabel>
        <HCard className="mb-2">
          {renamingHigloOnHearth ? (
            <div className="px-4 py-3">
              <input
                value={pendingHigloLabel}
                onChange={(e) => setPendingHigloLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRenameHigloOnHearth();
                  if (e.key === 'Escape') cancelRenameHigloOnHearth();
                }}
                disabled={savingHigloLabel}
                maxLength={40}
                autoFocus
                className="w-full text-[15px] font-medium text-slate-900 bg-white border border-slate-300 rounded px-2 py-1.5 outline-none focus:border-slate-900"
              />
              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                Referred to by this Hearth only.
              </p>
              <div className="flex justify-end gap-2 mt-2">
                <HGhostButton
                  onClick={cancelRenameHigloOnHearth}
                  disabled={savingHigloLabel}
                  className="text-[12px] px-3"
                >
                  Cancel
                </HGhostButton>
                <HPrimaryButton
                  onClick={saveRenameHigloOnHearth}
                  disabled={savingHigloLabel}
                  className="min-h-0 py-1.5 px-3 text-[12px]"
                >
                  {savingHigloLabel ? 'Saving…' : 'Save'}
                </HPrimaryButton>
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0 text-[15px] font-medium text-slate-900 truncate">
                {thisHearthHigloLabel || 'Higlo'}
              </div>
              <HGhostButton
                onClick={beginRenameHigloOnHearth}
                className="text-[11px] py-1 px-2 flex-shrink-0"
              >
                Rename
              </HGhostButton>
            </div>
          )}
        </HCard>
        <p className="text-[11px] text-slate-500 leading-relaxed mb-5">
          Renames here only change what this Hearth shows. Other Hearths keep their own names.
        </p>

        <SectionLabel className="mb-2">Leave this Higlo</SectionLabel>
        <HSecondaryButton
          onClick={removeHearthFromHiglo}
          disabled={saving || !!leaveBlockedReason}
          className="w-full text-[13px] text-slate-600"
          style={{ paddingTop: '10px', paddingBottom: '10px' }}
        >
          {pending === 'remove' ? 'Removing…' : 'Remove this Hearth from this Higlo'}
        </HSecondaryButton>
        {leaveBlockedReason && (
          <p className="text-[11px] text-slate-500 leading-relaxed text-center mt-2">
            {leaveBlockedReason}
          </p>
        )}
        {removeError && (
          <p className="text-[12px] text-red-600 leading-relaxed text-center mt-2">
            {removeError}
          </p>
        )}
      </div>
    </DetailShell>
  );
}

function BigPositionMini({ position, inverted }) {
  const slots = ['Left', 'Centre', 'Right'];
  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded border border-slate-200 flex-shrink-0',
        inverted ? 'bg-white' : 'bg-slate-50',
      )}
      style={{ padding: '6px 8px' }}
      aria-label={`Position ${position}`}
    >
      {slots.map((s) => (
        <span
          key={s}
          className={cn(
            'rounded-full block',
            position === s ? 'bg-slate-900' : 'bg-slate-200',
          )}
          style={{ width: 7, height: 7 }}
        />
      ))}
    </div>
  );
}
